/**
 * Where a booking came from, as one value the admin can read at a glance.
 *
 * Separate from `source`, which says which of our surfaces took the booking
 * (main site, a microsite, the app, the back office). This says how the visitor
 * reached us in the first place: an ad, a search, a social app, a link someone
 * sent them.
 */
export const TRAFFIC_SOURCES = [
  "google_ads",
  "meta_ads",
  "google_organic",
  "other_search",
  "instagram",
  "tiktok",
  "facebook",
  "youtube",
  "pinterest",
  "whatsapp",
  "ai_assistant",
  "referral",
  "direct",
] as const;

export type TrafficSource = (typeof TRAFFIC_SOURCES)[number];

export function isTrafficSource(value: unknown): value is TrafficSource {
  return (
    typeof value === "string" && (TRAFFIC_SOURCES as readonly string[]).includes(value)
  );
}

export interface TrafficSignals {
  /** document.referrer at landing — empty for direct hits and in-app browsers. */
  referrer?: string | null;
  /** utm_source from the landing URL, when a campaign link carried one. */
  utmSource?: string | null;
  /** utm_medium from the landing URL: "paid" on our Meta ad links. */
  utmMedium?: string | null;
  /** navigator.userAgent — the only tell for Instagram/TikTok in-app browsers. */
  userAgent?: string | null;
  /** Google Ads click id, if one was present. Outranks the referrer. */
  gclid?: string | null;
  /** Our own hostname, so internal navigation isn't mistaken for a referral. */
  self?: string | null;
}

/**
 * In-app browsers of the social apps. These strip the referrer, so the
 * User-Agent is the only evidence that a visit came from the app — which is
 * exactly the case ("did they come from the TikTok app?") the referrer misses.
 */
const IN_APP_PATTERNS: Array<[RegExp, TrafficSource]> = [
  [/instagram/i, "instagram"],
  // TikTok's webview reports musical_ly (the old app name) plus BytedanceWebview.
  [/musical_ly|bytedancewebview|tiktok/i, "tiktok"],
  [/\bFBAN\/|\bFBAV\/|\bFB_IAB\//, "facebook"],
];

/** utm_source values normalised onto our channels. */
const UTM_ALIASES: Record<string, TrafficSource> = {
  ig: "instagram",
  insta: "instagram",
  instagram: "instagram",
  tiktok: "tiktok",
  tt: "tiktok",
  fb: "facebook",
  facebook: "facebook",
  meta: "facebook",
  youtube: "youtube",
  yt: "youtube",
  pinterest: "pinterest",
  whatsapp: "whatsapp",
  wa: "whatsapp",
  google: "google_organic",
  bing: "other_search",
  newsletter: "referral",
  email: "referral",
  // Chat assistants open links with no referrer and only tag utm_source, so
  // without these their visits all landed in "direct".
  "chatgpt.com": "ai_assistant",
  chatgpt: "ai_assistant",
  openai: "ai_assistant",
  "perplexity.ai": "ai_assistant",
  perplexity: "ai_assistant",
  "claude.ai": "ai_assistant",
  claude: "ai_assistant",
  gemini: "ai_assistant",
  "gemini.google.com": "ai_assistant",
  copilot: "ai_assistant",
  "copilot.microsoft.com": "ai_assistant",
};

/** utm_medium values that mark a paid click rather than an organic post. */
const PAID_MEDIUMS = new Set(["paid", "paid_social", "paidsocial", "cpc", "ppc", "ads"]);

/** Referring hosts, matched on the registrable part so subdomains count. */
const HOST_PATTERNS: Array<[RegExp, TrafficSource]> = [
  // Before the search engines: gemini.google.com would otherwise match google.
  [
    /(^|\.)(chatgpt\.com|chat\.openai\.com|openai\.com|perplexity\.ai|claude\.ai|gemini\.google\.com|copilot\.microsoft\.com)$/,
    "ai_assistant",
  ],
  [/(^|\.)google\./, "google_organic"],
  [/(^|\.)(bing|duckduckgo|yahoo|ecosia|yandex|qwant|brave)\./, "other_search"],
  [/(^|\.)instagram\.com$/, "instagram"],
  [/(^|\.)tiktok\.com$/, "tiktok"],
  [/(^|\.)(facebook\.com|fb\.me|fb\.com|messenger\.com)$/, "facebook"],
  [/(^|\.)(youtube\.com|youtu\.be)$/, "youtube"],
  [/(^|\.)(pinterest\.[a-z.]+|pin\.it)$/, "pinterest"],
  [/(^|\.)(whatsapp\.com|wa\.me)$/, "whatsapp"],
];

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return null;
  }
}

/**
 * Reduce the landing signals to a single channel.
 *
 * Returns null when the visit came from our own site — the caller should keep
 * whatever first touch it already recorded rather than overwrite it with an
 * internal navigation.
 */
export function deriveTrafficSource(signals: TrafficSignals): TrafficSource | null {
  const { referrer, utmSource, utmMedium, userAgent, gclid, self } = signals;

  // A click id means an ad was paid for, whatever else the page says.
  if (gclid && gclid.trim().length > 0) return "google_ads";

  // Meta sends no click id we can rely on (fbclid rides on organic links
  // too), so our Meta ads carry utm_source=meta&utm_medium=paid. Checked
  // before the in-app browser test, which would otherwise call every ad click
  // made inside Instagram plain "instagram".
  const utmChannel = utmSource ? UTM_ALIASES[utmSource.trim().toLowerCase()] : undefined;
  if (
    (utmChannel === "facebook" || utmChannel === "instagram") &&
    utmMedium &&
    PAID_MEDIUMS.has(utmMedium.trim().toLowerCase())
  ) {
    return "meta_ads";
  }

  // Our Google Ads account appends utm_source=google&utm_medium=cpc to every
  // final URL. A click that arrives without a gclid (iOS sends gbraid/wbraid
  // instead) is still a paid click, not an organic search.
  if (utmChannel === "google_organic" && utmMedium && PAID_MEDIUMS.has(utmMedium.trim().toLowerCase())) {
    return "google_ads";
  }

  if (userAgent) {
    for (const [pattern, source] of IN_APP_PATTERNS) {
      if (pattern.test(userAgent)) return source;
    }
  }

  if (utmSource) {
    const alias = UTM_ALIASES[utmSource.trim().toLowerCase()];
    if (alias) return alias;
  }

  if (!referrer || referrer.trim().length === 0) return "direct";

  const host = hostOf(referrer);
  if (!host) return "direct";

  const selfHost = self?.replace(/^www\./, "").toLowerCase();
  if (selfHost && (host === selfHost || host.endsWith(`.${selfHost}`))) return null;

  for (const [pattern, source] of HOST_PATTERNS) {
    if (pattern.test(host)) return source;
  }

  return "referral";
}

/** Longest ad campaign value we keep; matches `reservations.ad_campaign`. */
export const AD_CAMPAIGN_MAX_LENGTH = 64;

/**
 * Clean a `utm_campaign` value before it is stored or sent.
 *
 * Our Google Ads account appends `utm_campaign={campaignid}`, so the value is
 * normally a numeric campaign id; other ad platforms send their own names.
 * Trimmed, capped at 64 characters, and limited to letters, digits and
 * `_ . : -` so a hand-edited URL or cookie cannot smuggle markup or spaces
 * into the admin. Returns null when nothing usable is left.
 */
export function sanitizeAdCampaign(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim().slice(0, AD_CAMPAIGN_MAX_LENGTH);
  if (value.length === 0) return null;
  return /^[A-Za-z0-9_.:-]+$/.test(value) ? value : null;
}

/**
 * Which chat assistant sent an `ai_assistant` visit. Kept apart from
 * TRAFFIC_SOURCES so filters and stats still group every assistant as one
 * channel, while the admin can say "AI chat · ChatGPT" on a single booking.
 */
export const AI_ASSISTANTS = ["chatgpt", "perplexity", "claude", "gemini", "copilot"] as const;

export type AiAssistant = (typeof AI_ASSISTANTS)[number];

export const AI_ASSISTANT_LABELS: Record<AiAssistant, string> = {
  chatgpt: "ChatGPT",
  perplexity: "Perplexity",
  claude: "Claude",
  gemini: "Gemini",
  copilot: "Copilot",
};

export function isAiAssistant(value: unknown): value is AiAssistant {
  return typeof value === "string" && (AI_ASSISTANTS as readonly string[]).includes(value);
}

const AI_UTM_ALIASES: Record<string, AiAssistant> = {
  "chatgpt.com": "chatgpt",
  chatgpt: "chatgpt",
  openai: "chatgpt",
  "perplexity.ai": "perplexity",
  perplexity: "perplexity",
  "claude.ai": "claude",
  claude: "claude",
  gemini: "gemini",
  "gemini.google.com": "gemini",
  copilot: "copilot",
  "copilot.microsoft.com": "copilot",
};

const AI_HOST_PATTERNS: Array<[RegExp, AiAssistant]> = [
  [/(^|\.)(chatgpt\.com|openai\.com)$/, "chatgpt"],
  [/(^|\.)perplexity\.ai$/, "perplexity"],
  [/(^|\.)claude\.ai$/, "claude"],
  [/(^|\.)gemini\.google\.com$/, "gemini"],
  [/(^|\.)copilot\.microsoft\.com$/, "copilot"],
];

/**
 * Name the assistant behind a visit, from the same landing signals
 * `deriveTrafficSource` reads. Null unless that call would say `ai_assistant`.
 */
export function deriveAiAssistant(signals: TrafficSignals): AiAssistant | null {
  if (deriveTrafficSource(signals) !== "ai_assistant") return null;

  const utm = signals.utmSource?.trim().toLowerCase();
  if (utm && AI_UTM_ALIASES[utm]) return AI_UTM_ALIASES[utm];

  const host = signals.referrer ? hostOf(signals.referrer) : null;
  if (host) {
    for (const [pattern, assistant] of AI_HOST_PATTERNS) {
      if (pattern.test(host)) return assistant;
    }
  }
  return null;
}
